import type { CategorySummaryDto } from '../types';
import './StatusPage.css';

interface PipelineStatsProps {
  categories: CategorySummaryDto[];
}

export function PipelineStats({ categories }: PipelineStatsProps) {
  const totalFeeds = categories.reduce((sum, c) => sum + c.stats.sourceCount, 0);
  const totalItems = categories.reduce((sum, c) => sum + c.stats.rssItemsFetched, 0);
  const totalArticles = categories.reduce((sum, c) => sum + c.stats.articlesSuccessful, 0);
  const totalClusters = categories.reduce((sum, c) => sum + c.stats.clusterCount, 0);
  const successfulSummaries = categories.filter(c => c.summary !== null).length;

  const stats = [
    { label: 'RSS Feeds', value: totalFeeds, color: 'accent' },
    { label: 'Items Fetched', value: totalItems, color: 'default' },
    { label: 'Articles Scraped', value: totalArticles, color: 'success' },
    { label: 'Story Clusters', value: totalClusters, color: 'default' },
    { label: 'Summaries Generated', value: successfulSummaries, color: 'accent' },
  ];

  // Group categories by category slug
  const categoryGroups = categories.reduce((acc, category) => {
    if (!acc[category.categorySlug]) {
      acc[category.categorySlug] = {
        slug: category.categorySlug,
        name: category.categoryName,
        items: [],
      };
    }
    acc[category.categorySlug].items.push(category);
    return acc;
  }, {} as Record<string, { slug: string; name: string; items: CategorySummaryDto[] }>);

  return (
    <div className="pipeline-stats-section">
      <div className="pipeline-stats">
        {stats.map((stat) => (
          <div key={stat.label} className={`pipeline-stats__card pipeline-stats__card--${stat.color}`}>
            <div className="pipeline-stats__value">{stat.value.toLocaleString()}</div>
            <div className="pipeline-stats__label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="pipeline-stats__category-cards">
        {Object.values(categoryGroups).map((group) => {
          const categoryFeeds = group.items.reduce((sum, c) => sum + c.stats.sourceCount, 0);
          const categoryItems = group.items.reduce((sum, c) => sum + c.stats.rssItemsFetched, 0);
          const categoryArticles = group.items.reduce((sum, c) => sum + c.stats.articlesSuccessful, 0);
          const categoryClusters = group.items.reduce((sum, c) => sum + c.stats.clusterCount, 0);
          const categorySummaries = group.items.filter(c => c.summary !== null).length;

          return (
            <div key={group.slug} className="pipeline-stats__category-card">
              <h4 className="pipeline-stats__category-title">{group.name}</h4>
              <div className="pipeline-stats__category-grid">
                <div className="pipeline-stats__category-stat">
                  <span className="pipeline-stats__category-value">{categoryFeeds}</span>
                  <span className="pipeline-stats__category-label">Feeds</span>
                </div>
                <div className="pipeline-stats__category-stat">
                  <span className="pipeline-stats__category-value">{categoryItems}</span>
                  <span className="pipeline-stats__category-label">Items</span>
                </div>
                <div className="pipeline-stats__category-stat">
                  <span className="pipeline-stats__category-value">{categoryArticles}</span>
                  <span className="pipeline-stats__category-label">Articles</span>
                </div>
                <div className="pipeline-stats__category-stat">
                  <span className="pipeline-stats__category-value">{categoryClusters}</span>
                  <span className="pipeline-stats__category-label">Clusters</span>
                </div>
                <div className="pipeline-stats__category-stat">
                  <span className="pipeline-stats__category-value">{categorySummaries}</span>
                  <span className="pipeline-stats__category-label">Summaries</span>
                </div>
              </div>
              {group.items.length > 1 && (
                <div className="pipeline-stats__category-subcats">
                  {group.items.filter(c => c.subCategorySlug).map(c => (
                    <span key={c.subCategorySlug} className="pipeline-stats__category-subcat">
                      {c.subCategoryName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
